import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewOtherFees } from '../../hooks/use-get-default-values';
import {
  ProductPurchaseFormType,
  ProductPurchaseOtherFeesFormType,
} from '../form-type';

function useGetOtherFeesColumns(): IColumn<ProductPurchaseOtherFeesFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();

  const columns = React.useMemo(
    () => [
      {
        header: t('common:name'),
        accessorKey: 'name',
        minSize: 200,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 400,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 200,
        dataType: ColumnDataType.number,
        minNumber: 0,
      },
      ...deleteColumn(editable),
    ],
    [editable, t],
  );

  return columns;
}

export default function ProductPurchaseOtherFeesTable() {
  const name = 'productPurchaseOtherFees' as const;
  const { editable } = useFormState();
  const { control, trigger, getValues, clearErrors } =
    useFormContext<ProductPurchaseFormType>();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });
  const columns = useGetOtherFeesColumns();

  const dispatch = React.useCallback(
    (action) => {
      const columnId =
        action.columnId as keyof ProductPurchaseOtherFeesFormType;
      const type = action.type as ActionType;
      const value = action.value;
      const currentRowIndex = action.rowIndex as number;
      const items = getValues(name);
      const item = items[currentRowIndex];

      const otherVal: () => Partial<ProductPurchaseOtherFeesFormType> = () => {
        return {};
      };

      if (type === 'addRow') {
        append(addNewOtherFees());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...item,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
